import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { User } from '@ia/shared';
import { api, ApiError, setUnauthorizedHandler } from './api';

type AuthState = {
  /** undefined = still loading */
  user: User | null | undefined;
  needsSetup: boolean | undefined;
  bootError: string | null;
  setUser: (u: User | null) => void;
  setNeedsSetup: (v: boolean) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [needsSetup, setNeedsSetup] = useState<boolean | undefined>(undefined);
  const [bootError, setBootError] = useState<string | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setBootError(null);
    try {
      const s = await api.setupStatus();
      setNeedsSetup(s.needsSetup);
      if (s.needsSetup) {
        setUser(null);
        return;
      }
      try {
        const me = await api.me();
        setUser(me.user);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) setUser(null);
        else throw e;
      }
    } catch (e) {
      setBootError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      void api
        .setupStatus()
        .then((s) => {
          setNeedsSetup(s.needsSetup);
          navigate(s.needsSetup ? '/setup' : '/login', { replace: true, state: { from: location.pathname + location.search } });
        })
        .catch(() => navigate('/login', { replace: true }));
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const value = useMemo(
    () => ({ user, needsSetup, bootError, setUser, setNeedsSetup, logout, refresh }),
    [user, needsSetup, bootError, logout, refresh],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

export function useIsAdmin(): boolean {
  return useAuth().user?.role === 'admin';
}
